import { action } from '@ember/object';
import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import type AbilitiesService from 'ember-can/services/abilities';
import type Entry from 'garaje/models/entry';
import type { LocationOverviewVisitorLogRouteParams } from 'garaje/pods/location-overview/visitor-log/route';
import type CurrentLocationService from 'garaje/services/current-location';
import type { RecordArray } from 'garaje/utils/type-utils';
import { hash } from 'rsvp';

import type MyLocationsVisitorLogController from './controller';

const SORT_COL_TO_FIELD_NAME = {
  Host: 'host',
  'Signed in': 'finalized_at',
  Name: 'full_name',
  Location: 'locations.name',
};

const ORDER_DESC_VALS = ['Z-A', 'Most to least recent'];
const PAGE_SIZE = 30;

export interface MyLocationsVisitorLogRouteModel {
  entries: RecordArray<Entry>;
  params: {
    include?: string;
    sort?: string;
    filter?: {
      query?: string;
      startDate?: string;
      endDate?: string;
      location?: string;
      status?: string;
    };
    page?: {
      offset: number;
      size: number;
      limit: number;
    };
  };
}

export default class MyLocationsVisitorLog extends Route {
  @service declare abilities: AbilitiesService;
  @service declare currentLocation: CurrentLocationService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  queryParams = {
    pageNumber: {
      refreshModel: true,
    },
    pageSize: {
      refreshModel: true,
    },
    sortBy: {
      refreshModel: true,
    },
    sortDirection: {
      refreshModel: true,
    },
    query: {
      refreshModel: true,
    },
    locationIds: {
      refreshModel: true,
    },
    signedOut: {
      refreshModel: true,
    },
    startDate: {
      refreshModel: true,
    },
    endDate: {
      refreshModel: true,
    },
  };

  beforeModel(): void {
    if (this.abilities.cannot('visit my-locations')) {
      this.router.transitionTo('unauthorized');
    }
  }

  model({
    endDate,
    locationIds,
    pageNumber,
    query,
    signedOut,
    sortBy,
    sortDirection,
    startDate,
  }: LocationOverviewVisitorLogRouteParams): Promise<MyLocationsVisitorLogRouteModel> {
    const params: MyLocationsVisitorLogRouteModel['params'] = {};
    const offset = PAGE_SIZE * pageNumber - PAGE_SIZE;
    const store = this.store;
    const orderDescending = ORDER_DESC_VALS.includes(sortDirection);
    const sortOrder = orderDescending ? '-' : '';
    const sortProp = SORT_COL_TO_FIELD_NAME[sortBy];

    if (sortBy) {
      params['sort'] = `${sortOrder}${sortProp}`;
    }

    params['filter'] = {};

    if (query) {
      params.filter.query = query;
    }

    if (startDate) {
      params.filter.startDate = startDate;
    }

    if (endDate) {
      params.filter.endDate = endDate;
    }

    params.page = { offset, size: PAGE_SIZE, limit: PAGE_SIZE };

    if (locationIds) {
      params.filter.location = locationIds;
    }

    if (signedOut) {
      params.filter.status = signedOut;
    }

    return hash({ entries: store.query('entry', params), params });
  }

  @action
  loading(transition: Transition): void {
    // eslint-disable-next-line ember/no-controller-access-in-routes
    const controller = <MyLocationsVisitorLogController>this.controllerFor('my-locations/visitor-log');
    controller.loadingData = true;
    void transition.promise.finally(function () {
      controller.loadingData = false;
    });
  }
}
